import { useParams } from "react-router-dom";
import { Cell, Row } from "react-table";
import { UnionServiceType } from "src/api/adminMemo/adminmemo-types";
import { CommonFacilityModel } from "src/api/building/building-types";
import { AvailableReservationTime, ReservationPolicyMode, UnitReservationTime } from "src/api/product/product-api";
import { ReservationPolicyModel } from "src/api/product/product-types";
import NoImage from "src/assets/images/NoImage.png";
import { BaseButton, BaseTooltip } from "src/components";
import BaseNewTabLink from "src/components/BaseNewTabLink";
import usePostCommonFacility from "src/hooks/product/usePostCommonFacility";
import { useQueryParams } from "src/hooks/useQueryParams";
import { PagePath } from "src/pages/product/details";
import { SpaceTypeLabel, SpaceTypeT } from "src/pages/product/product-types";
import { useModal } from "src/recoil/modalState/hook";
import { resizedImageUrl } from "src/utils";

import DefaultSettingModal from "../modal/DefaultSettingModal";
import { convertMinutesToDHMS } from "../libs";
import { ContractFacilityTableData } from "src/pages/contract/commonComponents/reservation/components/ContractSpaceContents";
import { DetailTotalData } from "src/api/contract/contract-types";
import { deleteOperationFacilityAsync } from "src/api/contract/contract-facility-api";
import { useApiOperation } from "src/api/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { ClosingTimePolicyModel } from "src/api/product/product-types";
import { RESERVATION_POLICY_QUERY_KEY } from "src/hooks/product/useGetReservationPolicy";
import { findFacilityReservationPolicy } from "../utils/reservationPolicy";
import {
  findFacilityClosingPolicy,
  flattenClosingPolicy,
  formatClosingPeriodLabel,
  formatClosingTimeLabel,
  isExclusiveClosingPolicy,
} from "../utils/closingTime";

export interface ReservationProps {
  row: Row<CommonFacilityModel> | Row<ContractFacilityTableData> | any; //상품 or 신청계약
  serviceType: UnionServiceType;
  serviceId: string;
  availableReservationTimeList: AvailableReservationTime[];
  unitReservationTimeList: UnitReservationTime[];
  closingTimePolicyList: ClosingTimePolicyModel[];
  reservationPolicyModeList: ReservationPolicyMode[];
  usageLimitPolicyList: ReservationPolicyModel[];

  detailData: DetailTotalData; //신청계약 전용
  [key: string]: any;
}

export const meeting_cols: any[] = [
  {
    // 건물 ID 컬럼 추가 (선택 시 건물 상세로 이동)
    Header: "건물 ID",
    accessor: "buildingId",
    width: 80,
    Cell: ({ value }: Cell<any>) => {
      if (!value) return <p>-</p>;
      const path = PagePath.building.detail.replace(":id", String(value));
      return <BaseNewTabLink path={path} value={String(value)} tooltip></BaseNewTabLink>;
    },
  },
  {
    // 건물 → 건물 명 (데이터 값 선택 미제공: 링크 제거)
    Header: "건물 명",
    accessor: "buildingName",
    width: 148,
    Cell: ({ value }: Cell<any>) => <BaseTooltip contents={value || "-"} />,
  },
  {
    // 회의실 id → 공용공간 ID (선택 시 해당 회의실 목록: 건물 > 공용공간 > 회의실 새 창)
    Header: "공용공간 ID",
    accessor: "id",
    width: 80,
    Cell: ({ value, row }: Cell<any>) => {
      const path = PagePath.building.detail.replace(":id", row.original.buildingId);
      return <BaseNewTabLink path={path + "?tab=facility&facilityType=MEETING_ROOM"} value={value || "-"} tooltip></BaseNewTabLink>;
    },
  },
  {
    // 회의실 명: 링크 없이 이름만 출력
    Header: "회의실 명",
    accessor: "facilityName",
    width: 148,
    Cell: ({ value }: Cell<any>) => <BaseTooltip contents={value || "-"} />,
  },
  {
    Header: "이미지",
    accessor: "img",
    width: 70,

    Cell: ({ row, value }: Cell<any>) => {
      return (
        <div className="flex-center-center">
          <img
            className="table_img"
            onError={(e) => {
              const img = e.currentTarget;
              const originalUrl = row.original?.mediaList?.[0]?.url;
              // resized 실패 → 원본 1회 시도 → 그래도 실패 시 NoImage로 종착 (재발동 시 무한 재요청 방지)
              if (originalUrl && img.src !== originalUrl) {
                img.src = originalUrl;
              } else {
                img.onerror = null;
                img.src = NoImage;
              }
            }}
            src={resizedImageUrl(row.original?.mediaList?.[0]?.url) || NoImage}
            alt=""
          ></img>
        </div>
      );
    },
  },
  {
    Header: "위치",
    accessor: "floorNum",
    width: 96,
    Cell: ({ row, value }: Cell<any>) => {
      const isGround = row.original.isGround;
      return (
        <p>
          {isGround ? "지상" : "지하"} {value || "-"}층
        </p>
      );
    },
  },
];

export const refresh_cols: any[] = [
  {
    // 건물 ID 컬럼 추가 (선택 시 건물 상세로 이동)
    Header: "건물 ID",
    accessor: "buildingId",
    width: 80,
    Cell: ({ value }: Cell<any>) => {
      if (!value) return <p>-</p>;
      const path = PagePath.building.detail.replace(":id", String(value));
      return <BaseNewTabLink path={path} value={String(value)} tooltip></BaseNewTabLink>;
    },
  },
  {
    // 건물 → 건물 명 (데이터 값 선택 미제공: 링크 제거)
    Header: "건물 명",
    accessor: "buildingName",
    width: 148,
    Cell: ({ value }: Cell<any>) => <BaseTooltip contents={value || "-"} />,
  },
  {
    // 편의시설 id → 공용공간 ID (선택 시 해당 편의시설 목록: 건물 > 공용공간 > 편의시설 새 창)
    Header: "공용공간 ID",
    accessor: "id",
    width: 80,
    Cell: ({ value, row }: Cell<any>) => {
      const path = PagePath.building.detail.replace(":id", row.original.buildingId);
      return <BaseNewTabLink path={path + "?tab=facility&facilityType=REFRESH_ROOM"} value={value || "-"} tooltip></BaseNewTabLink>;
    },
  },
  {
    // 편의시설 명: 링크 없이 이름만 출력
    Header: "편의시설 명",
    accessor: "facilityName",
    width: 148,
    Cell: ({ value }: Cell<any>) => <BaseTooltip contents={value || "-"} />,
  },

  {
    Header: "위치",
    accessor: "floorNum",
    width: 80,
    Cell: ({ row, value }: Cell<any>) => {
      const isGround = row.original.isGround;
      return (
        <p>
          {isGround ? "지상" : "지하"} {value || "-"}층
        </p>
      );
    },
  },
];

export const desk_cols: any[] = [
  {
    // 건물 ID 컬럼 추가 (선택 시 건물 상세로 이동)
    Header: "건물 ID",
    accessor: "buildingId",
    width: 80,
    Cell: ({ value }: Cell<any>) => {
      if (!value) return <p>-</p>;
      const path = PagePath.building.detail.replace(":id", String(value));
      return <BaseNewTabLink path={path} value={String(value)} tooltip></BaseNewTabLink>;
    },
  },
  {
    // 건물 → 건물 명 (데이터 값 선택 미제공: 링크 제거)
    Header: "건물 명",
    accessor: "buildingName",
    width: 148,
    Cell: ({ value }: Cell<any>) => <BaseTooltip contents={value || "-"} />,
  },
  {
    // id → 공용공간 ID (선택 시 해당 좌석 목록: 건물 > 공용공간 > 좌석 새 창)
    Header: "공용공간 ID",
    accessor: "id",
    width: 80,
    Cell: ({ value, row }: Cell<any>) => {
      const path = PagePath.building.detail.replace(":id", row.original.buildingId);
      return <BaseNewTabLink path={path + "?tab=facility&facilityType=DESK"} value={value || "-"} tooltip></BaseNewTabLink>;
    },
  },
  {
    Header: "좌석그룹 명",
    accessor: "groupName",
    width: 146,
    Cell: ({ row, value, serviceType }: ReservationProps) => {
      if (serviceType === "SERVICE_PRODUCT") {
        //상품
        return (
          <div className="flex-center-start w100">
            <BaseTooltip contents={value || "-"} />
          </div>
        );
      } else {
        //신청계약
        if ("buildingCommonFacilityDeskGroupName" in row.original) {
          //타입가드
          return (
            <div className="flex-center-start w100">
              <BaseTooltip contents={row?.original?.buildingCommonFacilityDeskGroupName || "-"} />
            </div>
          );
        }
      }
    },
  },
  {
    // 좌석 명: 링크 없이 이름만 출력
    Header: "좌석 명",
    accessor: "facilityName",
    width: 96,
    Cell: ({ value }: Cell<any>) => <BaseTooltip contents={value || "-"} />,
  },
  {
    Header: "위치",
    accessor: "floorNum",
    width: 80,
    Cell: ({ row, value }: Cell<any>) => {
      const isGround = row.original.isGround;
      return (
        <p>
          {isGround ? "지상" : "지하"} {value || "-"}층
        </p>
      );
    },
  },
];

export const duplicate_cols: any[] = [
  {
    Header: "예약 가능 기간",
    accessor: "time",
    width: 175,
    Cell: ({ row, availableReservationTimeList, reservationPolicyModeList }: ReservationProps) => {
      const PolicyMode = reservationPolicyModeList?.find(
        (data) =>
          String(data.buildingCommonFacilityId) === String(row.original?.id) &&
          data.reservationPolicyModeType === "RESERVATION_POLICY_MODE_AVAILABLE",
      );
      const availableReservationTime = availableReservationTimeList?.find(
        (data) => String(data.buildingCommonFacilityId) === String(row.original?.id),
      );

      const TimeStart = convertMinutesToDHMS(availableReservationTime?.availableReservationTimeStart);
      const TimeEnd = convertMinutesToDHMS(availableReservationTime?.availableReservationTimeEnd);

      if (PolicyMode?.isDefault !== false) {
        return <div>기본설정</div>;
      }

      return (
        <div>
          {!availableReservationTime?.isLimitedAvailableReservationTime ? (
            <div>무제한</div>
          ) : (
            <div className="w-100 flex-center-start">
              {TimeStart && <span>{TimeStart} 이후</span>}
              {TimeStart && TimeEnd && " - "}
              {TimeEnd && <span>{TimeEnd} 이내</span>}
            </div>
          )}
        </div>
      );
    },
  },
  {
    Header: "예약 시간 단위",
    accessor: "unit",
    width: 175,
    Cell: ({ row, unitReservationTimeList, reservationPolicyModeList }: ReservationProps) => {
      const PolicyMode = reservationPolicyModeList?.find(
        (data) =>
          String(data.buildingCommonFacilityId) === String(row.original?.id) && data.reservationPolicyModeType === "RESERVATION_POLICY_MODE_UNIT",
      );

      const unitReservationTime = unitReservationTimeList?.find((data) => String(data.buildingCommonFacilityId) === String(row.original?.id));

      const minTime = convertMinutesToDHMS(unitReservationTime?.minimumReservationTime);
      const maxTime = convertMinutesToDHMS(unitReservationTime?.maximumReservationTime);

      if (PolicyMode?.isDefault !== false) {
        return <div>기본설정</div>;
      }

      return (
        <div>
          {!unitReservationTimeList?.[0]?.isLimitedUnitReservationTime ? (
            <div>무제한</div>
          ) : (
            <div className="w-100 flex-center-start">
              {minTime && <span>최소{minTime}</span>}
              {minTime && maxTime && " - "}
              {maxTime && <span>최대{maxTime}</span>}
            </div>
          )}
        </div>
      );
    },
  },
  {
    Header: "휴무일",
    accessor: "closed",
    width: 240,
    Cell: ({ row, closingTimePolicyList }: ReservationProps) => {
      const facilityPolicy = findFacilityClosingPolicy(closingTimePolicyList, row.original?.id);

      // exclusive=false 이거나 개별 정책이 없으면 공용공간 타입 기본 정책을 따른다
      if (!isExclusiveClosingPolicy(facilityPolicy)) {
        return <div>기본 설정</div>;
      }

      const arrText = flattenClosingPolicy(facilityPolicy).map(
        (recurrence) => `${formatClosingPeriodLabel(recurrence)} (${formatClosingTimeLabel(recurrence)})`,
      );

      if (arrText.length === 0) {
        return <div>-</div>;
      }

      if (arrText.length === 1) {
        return <div>{arrText[0]}</div>;
      }

      return <div>{arrText[0]} 외 {arrText.length - 1}건</div>;
    },
  },
  {
    Header: "월 이용한도",
    accessor: "usage",
    width: 252,
    Cell: ({ row, usageLimitPolicyList }: ReservationProps) => {
      const facilityId = row?.original?.id;
      const individualPolicy = findFacilityReservationPolicy(usageLimitPolicyList, "usage-limit-policy", facilityId);
      const usageLimitPolicy = individualPolicy?.details?.usageLimitPolicy;

      if (usageLimitPolicy?.disabled) {
        return <div>무제한</div>;
      }

      const timeEnabled = usageLimitPolicy?.time && !usageLimitPolicy.time.disabled && usageLimitPolicy.time.monthlyLimit && !usageLimitPolicy.time.monthlyLimit.disabled;
      const feeEnabled = usageLimitPolicy?.fee && !usageLimitPolicy.fee.disabled && usageLimitPolicy.fee.monthlyLimit && !usageLimitPolicy.fee.monthlyLimit.disabled;

      return (
        <div>
          {timeEnabled && <div>{convertMinutesToDHMS(usageLimitPolicy!.time.monthlyLimit.minutes) ?? "-"}</div>}
          {feeEnabled && <div>{`${Number(usageLimitPolicy!.fee.monthlyLimit.amount ?? 0).toLocaleString()}원`}</div>}
          {!timeEnabled && !feeEnabled && <div>무제한</div>}
        </div>
      );
    },
  },
  {
    Header: "일 이용한도",
    accessor: "usage2",
    width: 252,
    Cell: ({ row, usageLimitPolicyList }: ReservationProps) => {
      const facilityId = row?.original?.id;
      const individualPolicy = findFacilityReservationPolicy(usageLimitPolicyList, "usage-limit-policy", facilityId);
      const usageLimitPolicy = individualPolicy?.details?.usageLimitPolicy;

      if (usageLimitPolicy?.disabled) {
        return <div>무제한</div>;
      }

      const timeEnabled = usageLimitPolicy?.time && !usageLimitPolicy.time.disabled && usageLimitPolicy.time.dailyLimit && !usageLimitPolicy.time.dailyLimit.disabled;
      const feeEnabled = usageLimitPolicy?.fee && !usageLimitPolicy.fee.disabled && usageLimitPolicy.fee.dailyLimit && !usageLimitPolicy.fee.dailyLimit.disabled;

      return (
        <div>
          {timeEnabled && <div>{convertMinutesToDHMS(usageLimitPolicy!.time.dailyLimit.minutes) ?? "-"}</div>}
          {feeEnabled && <div>{`${Number(usageLimitPolicy!.fee.dailyLimit.amount ?? 0).toLocaleString()}원`}</div>}
          {!timeEnabled && !feeEnabled && <div>무제한</div>}
        </div>
      );
    },
  },
];

export const time_court_price_cols = [];

export const time_court_edit_cols = [];

export const time_court_delete_cols = [];

export const edit_cols: any[] = [
  {
    Header: "예약 정책",
    accessor: "edit",
    sticky: "right",
    width: 120,
    Cell: ({
      row,
      availableReservationTimeList,
      unitReservationTimeList,
      reservationPolicyModeList,
      serviceType,
      serviceId,

      detailData,
    }: ReservationProps) => {
      const { setAbstractModalZ1 } = useModal();
      const spaceType = row.original.commonFacilityType as SpaceTypeT;

      return (
        <div className="flex-row flex-center">
          <BaseButton
            title={"개별 설정"}
            className="color-white"
            disabled={
              detailData?.contract?.contractStep === "APPLY_CANCEL" ||
              detailData?.contract?.contractStep === "TERMINATE_COMPLETE" ||
              detailData?.contract?.contractStep === "APPLY_CONFIRM" ||
              detailData?.contract?.contractStep === "USE_COMPLETE"
            }
            onClick={() => {
              setAbstractModalZ1({
                size: "xlarge",
                className: "dialog-modal",
                isOpen: true,
                children: (
                  <DefaultSettingModal
                    spaceType={spaceType}
                    serviceType={serviceType}
                    serviceId={serviceId}
                    isDefault={false}
                    space={row?.original}
                    reservationPolicyModeList={reservationPolicyModeList}
                    detailData={detailData}
                  ></DefaultSettingModal>
                ),
              });
            }}
          />
        </div>
      );
    },
  },
];

export const delete_cols: any[] = [
  {
    Header: "삭제",
    accessor: "btn",
    sticky: "right",
    width: 48,
    Cell: ({ row, detailData, serviceType }: ReservationProps) => {
      const { setBaseModal } = useModal();
      const { mutate } = usePostCommonFacility();
      const { queryParams } = useQueryParams<{ id?: string }>();
      const params = useParams();
      const productId = queryParams.id ? queryParams.id : params.id;
      const queryClient = useQueryClient();
      const { executeAsync: deleteOperationFacility } = useApiOperation(deleteOperationFacilityAsync);
      const spaceType = row.original.commonFacilityType as SpaceTypeT;

      if (serviceType === "SERVICE_PRODUCT") {
        return (
          <button
            className="base-trash-btn color-bg-gray"
            onClick={() => {
              setBaseModal({
                isOpen: true,
                btnLeftTitle: "취소",
                btnRightTitle: "확인",
                title: `해당 ${SpaceTypeLabel[spaceType]}을 삭제합니다`,
                children: `삭제한 즉시 해당 ${SpaceTypeLabel[spaceType]}에 엑세스 할 수 없습니다`,
                onClick: () => {
                  mutate({
                    productBuildingCommonFacilitySaveList: [
                      {
                        id: row?.original?.productBuildingCommonFacilityId,
                        cmdType: "D",
                        buildingCommonFacilityId: row.original?.id?.toString(),
                      },
                    ],
                    productId: productId!,
                  });

                  setBaseModal({ isOpen: false });
                },
              });
            }}
          ></button>
        );
      } else {
        return (
          <button
            className="base-trash-btn color-bg-gray"
            disabled={
              detailData?.contract?.contractStep === "APPLY_CANCEL" ||
              detailData?.contract?.contractStep === "TERMINATE_COMPLETE" ||
              detailData?.contract?.contractStep === "APPLY_CONFIRM" ||
              detailData?.contract?.contractStep === "USE_COMPLETE"
            }
            onClick={() => {
              setBaseModal({
                isOpen: true,
                btnLeftTitle: "취소",
                btnRightTitle: "확인",
                title: `해당 ${SpaceTypeLabel[spaceType]}을 삭제합니다`,
                children: `삭제한 즉시 해당 ${SpaceTypeLabel[spaceType]}에 엑세스 할 수 없습니다`,
                onClick: () => {
                  deleteOperationFacility({ contractId: detailData.contract?.contractId!, facilityId: row.original.id }).finally(() => {
                    setBaseModal({ isOpen: false });

                    queryClient.refetchQueries({
                      queryKey: ["getContractOperationDetailSync"],
                    });
                    queryClient.refetchQueries({
                      queryKey: [RESERVATION_POLICY_QUERY_KEY],
                    });
                  });
                },
              });
            }}
          ></button>
        );
      }
    },
  },
];
